import { useCallback, useEffect, useState } from "react";
import type { OrgEntitlementRow, OrgSubscriptionStatus } from "@/lib/platform/orgSubscriptionCatalog";
import { listOrgSubscriptions, upsertOrgSubscription } from "@/lib/platform/orgSubscriptionsClient";

export function useOrgSubscriptions(organizationId: string) {
  const [rows, setRows] = useState<OrgEntitlementRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!organizationId) {
      setRows([]);
      setError(null);
      setLoading(false);
      return;
    }
    setRows([]);
    setLoading(true);
    setError(null);
    const res = await listOrgSubscriptions(organizationId);
    setLoading(false);
    if (!res.ok) {
      setRows([]);
      setError(res.error);
      return;
    }
    setRows(res.data.rows ?? []);
  }, [organizationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = useCallback(
    async (addonKey: string, status: OrgSubscriptionStatus, notes: string) => {
      if (!organizationId) return false;
      setBusyKey(addonKey);
      setError(null);
      const res = await upsertOrgSubscription({ organizationId, addonKey, status, notes });
      setBusyKey(null);
      if (!res.ok) {
        setError(res.error);
        return false;
      }
      setRows((current) => {
        const next = current.filter((row) => row.addon_key !== res.data.row.addon_key);
        next.push(res.data.row);
        next.sort((a, b) => a.addon_key.localeCompare(b.addon_key));
        return next;
      });
      return true;
    },
    [organizationId],
  );

  return { rows, loading, error, busyKey, save };
}
